import React, { useState, useEffect, useMemo } from 'react'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import MenuIcon from '@mui/icons-material/Menu'
import { Link, usePage } from '@inertiajs/react'
import { AppLayout } from '@/components/layouts/AppLayout'
import { SessionSidebar, SessionSidebarItem } from '@/components/monitoring/SessionSidebar'
import { SessionDetail, SessionDetailData } from '@/components/monitoring/SessionDetail'
import { useSessionListChannel } from '@/hooks/useSessionListChannel'

const MONITORING_ITEMS = [
  { label: 'Dashboard', href: '/monitoring/dashboard' },
  { label: 'Agent Status', href: '/monitoring/agent_statuses' },
  { label: 'Session Logs', href: '/monitoring/session_logs' },
  { label: 'Webhook Events', href: '/monitoring/webhook_events' },
]

interface Props {
  session_logs: SessionSidebarItem[]
  selected_session_log: SessionDetailData | null
  current_agent_is_admin: boolean
}

export default function Index({ session_logs, selected_session_log, current_agent_is_admin }: Props) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  useSessionListChannel()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const pageUrl = usePage().url || ''
  const currentPath = pageUrl.split('?')[0]

  // Parse ?entry=N query parameter for deep-linking to specific log entries
  const scrollToEntry = useMemo(() => {
    const match = pageUrl.match(/[?&]entry=(\d+)/)
    return match ? parseInt(match[1], 10) : null
  }, [pageUrl])

  // Close sidebar on mobile when a session is selected
  useEffect(() => {
    if (isMobile && selected_session_log) {
      setSidebarOpen(false)
    }
  }, [selected_session_log?.id, isMobile])

  const monitoringNav = (
    <List sx={{ pt: 2 }}>
      {MONITORING_ITEMS.map((item) => (
        <ListItemButton
          key={item.href}
          component={Link}
          href={item.href}
          selected={currentPath.startsWith(item.href)}
          onClick={() => isMobile && setSidebarOpen(false)}
          sx={{
            mx: 1,
            borderRadius: 1,
            '&.Mui-selected': {
              backgroundColor: 'action.selected',
            },
          }}
        >
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              variant: 'body2',
              fontWeight: currentPath.startsWith(item.href) ? 600 : 400,
            }}
          />
        </ListItemButton>
      ))}
    </List>
  )

  const sessionSidebarContent = (
    <SessionSidebar
      sessions={session_logs}
      selectedId={selected_session_log?.id ?? null}
    />
  )

  return (
    <AppLayout noPadding>
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
        {/* Monitoring navigation sidebar */}
        {!isMobile && (
          <Box
            sx={{
              width: 220,
              flexShrink: 0,
              borderRight: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            {monitoringNav}
          </Box>
        )}

        {/* Desktop session list sidebar */}
        {!isMobile && sessionSidebarContent}

        {/* Mobile drawer with monitoring nav + session list */}
        {isMobile && (
          <Drawer
            anchor="left"
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            PaperProps={{ sx: { width: 320 } }}
          >
            <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Monitoring
                </Typography>
              </Box>
              {monitoringNav}
            </Box>
            {sessionSidebarContent}
          </Drawer>
        )}

        {/* Detail area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
          {selected_session_log ? (
            <SessionDetail
              session={selected_session_log}
              onMenuClick={isMobile ? () => setSidebarOpen(true) : undefined}
              scrollToEntry={scrollToEntry}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                gap: 1,
              }}
            >
              {isMobile && (
                <IconButton onClick={() => setSidebarOpen(true)} sx={{ mb: 1, minWidth: 44, minHeight: 44 }}>
                  <MenuIcon />
                </IconButton>
              )}
              <Typography variant="body2" color="text.secondary">
                Select a session to view details
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </AppLayout>
  )
}
